import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  MapPin,
  Clock,
  Calendar,
  Building2,
  UserCheck,
  Briefcase,
  Bell,
  ClipboardCheck,
  FileText,
  Newspaper,
  MessageSquareDot,
  Settings,
  Landmark,
  Activity,
  FileBarChart,
  Target,
  Shield,
  UserCog,
  GraduationCap,
  Trophy,
  CalendarClock,
  ClipboardList,
  ChevronDown,
  Network,
  CalendarDays,
  BarChart3,
  HeartHandshake,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TCOSidebarLogo } from '../ui/TCOLogo';
import { usePermissions } from '../../hooks/usePermissions';
import { usePendingRequestsCount } from '../../hooks/schedule-change-requests/useScheduleChangeRequests';

type NavItem = {
  type: 'item';
  name: string;
  href: string;
  icon: LucideIcon;
  permission: string;
  adminOnly?: boolean;
};

type NavGroup = {
  type: 'group';
  name: string;
  icon: LucideIcon;
  items: NavItem[];
};

type NavEntry = NavItem | NavGroup;

const navigation: NavEntry[] = [
  { type: 'item', name: 'Панель управления', href: '/', icon: Home, permission: 'employees' },
  {
    type: 'group',
    name: 'Организационная структура',
    icon: Network,
    items: [
      { type: 'item', name: 'Организации', href: '/organizations', icon: Landmark, permission: 'organizations' },
      { type: 'item', name: 'Отделы', href: '/departments', icon: Building2, permission: 'departments' },
      { type: 'item', name: 'Должности', href: '/positions', icon: Briefcase, permission: 'positions' },
      { type: 'item', name: 'Локации', href: '/locations', icon: MapPin, permission: 'locations' },
      { type: 'item', name: 'Сотрудники', href: '/employees', icon: Users, permission: 'employees' },
    ],
  },
  {
    type: 'group',
    name: 'Графики и время',
    icon: CalendarDays,
    items: [
      { type: 'item', name: 'Расписания', href: '/schedules', icon: Calendar, permission: 'schedules' },
      { type: 'item', name: 'Управление графиками', href: '/schedule-management', icon: CalendarClock, permission: 'schedule_requests' },
      { type: 'item', name: 'Заявки на графики', href: '/schedule-change-requests', icon: ClipboardList, permission: 'schedule_requests' },
      { type: 'item', name: 'Smart Schedule', href: '/smart-schedule', icon: Sparkles, permission: 'smart_schedule' },
      { type: 'item', name: 'Замены смен', href: '/shift-replacements', icon: Users, permission: 'smart_schedule' },
      { type: 'item', name: 'Лимиты смен', href: '/staffing-limits', icon: Target, permission: 'staffing_limits' },
      { type: 'item', name: 'Посещаемость', href: '/attendance', icon: UserCheck, permission: 'attendance' },
      { type: 'item', name: 'Учет времени', href: '/time-records', icon: Clock, permission: 'time_records' },
    ],
  },
  {
    type: 'group',
    name: 'Аналитика',
    icon: BarChart3,
    items: [
      { type: 'item', name: 'Отчеты', href: '/reports', icon: FileBarChart, permission: 'reports' },
      { type: 'item', name: 'Журнал действий', href: '/activity-logs', icon: Activity, permission: 'activity_logs' },
    ],
  },
  {
    type: 'group',
    name: 'HR-процессы',
    icon: HeartHandshake,
    items: [
      { type: 'item', name: 'Тестирование', href: '/testing', icon: ClipboardCheck, permission: 'testing' },
      // Чек-листы временно скрыты до запуска модуля (вернуть вместе с маршрутом в App.tsx):
      // { type: 'item', name: 'Чек-листы', href: '/checklists', icon: ListChecks, permission: 'checklists' },
      { type: 'item', name: 'Документы', href: '/documents', icon: FileText, permission: 'documents' },
      { type: 'item', name: 'Новости', href: '/news', icon: Newspaper, permission: 'news' },
      { type: 'item', name: 'Обратная связь', href: '/feedback', icon: MessageSquareDot, permission: 'feedback' },
      { type: 'item', name: 'Уведомления', href: '/notifications', icon: Bell, permission: 'notifications' },
      { type: 'item', name: 'Геймификация', href: '/gamification', icon: Trophy, permission: 'gamification' },
    ],
  },
  { type: 'item', name: 'Мои тесты', href: '/my-tests', icon: GraduationCap, permission: 'my_tests' },
  {
    type: 'group',
    name: 'Администрирование',
    icon: Settings,
    items: [
      { type: 'item', name: 'Пользователи', href: '/users', icon: UserCog, permission: 'users' },
      { type: 'item', name: 'Роли', href: '/roles', icon: Shield, permission: 'settings', adminOnly: true },
      { type: 'item', name: 'Настройки', href: '/settings', icon: Settings, permission: 'settings' },
    ],
  },
];

const STORAGE_KEY = 'sidebar:expandedGroups';

function isPathActive(pathname: string, href: string): boolean {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(href + '/');
}

export function Sidebar() {
  const { hasPermission, isAdmin } = usePermissions();
  const { data: pendingCount } = usePendingRequestsCount();
  const location = useLocation();

  const isItemVisible = (item: NavItem): boolean => {
    if (isAdmin()) return true;
    if (item.adminOnly) return false;
    return hasPermission(item.permission, 'read');
  };

  const visibleEntries = navigation
    .map((entry): NavEntry | null => {
      if (entry.type === 'item') {
        return isItemVisible(entry) ? entry : null;
      }
      const visibleItems = entry.items.filter(isItemVisible);
      return visibleItems.length > 0 ? { ...entry, items: visibleItems } : null;
    })
    .filter((e): e is NavEntry => e !== null);

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed as Record<string, boolean>;
        }
      }
    } catch {
      // ignore localStorage errors
    }
    return {};
  });

  useEffect(() => {
    for (const entry of visibleEntries) {
      if (entry.type !== 'group') continue;
      const hasActive = entry.items.some((item) => isPathActive(location.pathname, item.href));
      if (hasActive && expanded[entry.name] === false) {
        setExpanded((prev) => ({ ...prev, [entry.name]: true }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(expanded));
    } catch {
      // ignore localStorage errors
    }
  }, [expanded]);

  const isGroupOpen = (name: string) => expanded[name] !== false;

  const toggleGroup = (name: string) => {
    setExpanded((prev) => ({ ...prev, [name]: !(prev[name] !== false) }));
  };

  const renderItem = (item: NavItem, indented = false) => (
    <li key={item.name}>
      <NavLink
        to={item.href}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 group',
            indented && 'pl-9',
            isActive
              ? 'gradient-blue text-white shadow-lg'
              : 'text-neutral-300 hover:text-white hover:bg-white/10'
          )
        }
        end={item.href === '/'}
      >
        <item.icon className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
        <span className="flex-1">{item.name}</span>
        {item.href === '/schedule-change-requests' && !!pendingCount && pendingCount > 0 && (
          <span className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold">
            {pendingCount}
          </span>
        )}
      </NavLink>
    </li>
  );

  return (
    <div className="w-[280px] bg-gradient-to-b from-neutral-700 to-neutral-800 flex-shrink-0 shadow-xl h-screen flex flex-col">
      <div className="px-6 py-6 border-b border-white/10 flex-shrink-0">
        <TCOSidebarLogo />
      </div>
      <nav className="px-4 py-6 overflow-y-auto flex-1 min-h-0">
        <ul className="space-y-1.5">
          {visibleEntries.map((entry) => {
            if (entry.type === 'item') return renderItem(entry);

            const open = isGroupOpen(entry.name);
            const collapsedGroupPending =
              !open && entry.items.some((i) => i.href === '/schedule-change-requests')
                ? pendingCount ?? 0
                : 0;

            return (
              <li key={entry.name} className="pt-2">
                <button
                  type="button"
                  onClick={() => toggleGroup(entry.name)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-neutral-400 hover:text-neutral-200 transition-colors"
                >
                  <entry.icon className="w-4 h-4" />
                  <span className="flex-1 text-left">{entry.name}</span>
                  {collapsedGroupPending > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold">
                      {collapsedGroupPending}
                    </span>
                  )}
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 transition-transform duration-200',
                      open ? 'rotate-0' : '-rotate-90'
                    )}
                  />
                </button>
                {open && (
                  <ul className="mt-1 space-y-1.5">
                    {entry.items.map((item) => renderItem(item, true))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
